import { formatInTimeZone } from "date-fns-tz";
import type { EventGroupAvailability } from "@/types/event";

export interface EventGroupFormRow {
  /** Present for waves that already exist — keeps their id (and attendees) on save. */
  id?: string;
  name: string;
  /** datetime-local value in the event's timezone */
  startTime: string;
  endTime: string;
  capacity: number | string;
  hostId: string;
  secondaryHostId: string;
  meetingVenue: string;
  whatsappGroup: string;
  displayOrder: number;
}

export const toNumber = (v: number | string | undefined | null) => {
  const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
  return Number.isFinite(n) ? n : 0;
};

/** Earliest wave by start time (then order) — where existing unassigned bookings land. */
export const earliestGroupIndex = (groups: EventGroupFormRow[]) => {
  if (groups.length === 0) return -1;
  let best = 0;
  groups.forEach((g, i) => {
    const b = groups[best];
    if (g.startTime < b.startTime || (g.startTime === b.startTime && g.displayOrder < b.displayOrder)) best = i;
  });
  return best;
};

/**
 * Same rules the set_event_groups RPC enforces, run first so the admin hears it from the form:
 * capacities sum to the event capacity, and no wave drops below what's already booked in it.
 * `unassignedBooked` are confirmed tickets with no wave yet; they join the earliest wave on save.
 */
export const validateEventGroups = (
  groups: EventGroupFormRow[],
  eventCapacity: number,
  bookedByGroupId: Record<string, number>,
  unassignedBooked = 0,
): string | null => {
  if (groups.length === 0) return null;
  for (const g of groups) {
    if (!g.name.trim()) return "Every wave needs a name";
    if (!g.startTime) return `Wave "${g.name}" needs a start time`;
  }
  const sum = groups.reduce((s, g) => s + toNumber(g.capacity), 0);
  if (sum !== eventCapacity) {
    return `Wave capacities add up to ${sum}, but the event capacity is ${eventCapacity}`;
  }
  const first = earliestGroupIndex(groups);
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    const booked = (g.id ? bookedByGroupId[g.id] ?? 0 : 0) + (i === first ? unassignedBooked : 0);
    if (toNumber(g.capacity) < booked) {
      return `Wave "${g.name}" already has ${booked} booked — its capacity can't be below that`;
    }
  }
  return null;
};

export const formatGroupTime = (iso: string, timezone = "Europe/London") =>
  formatInTimeZone(new Date(iso), timezone, "h:mm a");

export const sortEventGroups = (groups: EventGroupAvailability[]) =>
  [...groups].sort((a, b) =>
    a.start_time.localeCompare(b.start_time)
    || a.display_order - b.display_order
    || a.group_id.localeCompare(b.group_id)
  );

export const earliestOpenGroup = (groups: EventGroupAvailability[]) =>
  sortEventGroups(groups).find((group) => group.spots_remaining > 0) ?? null;

export { shiftAgendaTimes } from "@shared/departure";

type HostLike = { id: string; first_name?: string | null; last_name?: string | null; avatar_url?: string | null };

/**
 * Hosts leading one departure. A wave with its own host(s) shows only them;
 * a wave with no hosts set falls back to the event's hosts.
 */
export const resolveGroupHosts = <H extends HostLike>(
  group: { host_id: string | null; secondary_host_id: string | null; host?: H; secondary_host?: H },
  eventHosts: { primary?: H | null; secondary?: H | null }
): H[] => {
  if (!group.host_id && !group.secondary_host_id) {
    return [eventHosts.primary, eventHosts.secondary].filter((h): h is H => !!h);
  }
  return [group.host, group.secondary_host].filter((h): h is H => !!h);
};
